import React from 'react';
import PropTypes from 'prop-types';
import { computed, get, remove } from 'mobx';
import { observer } from 'mobx-react';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Tooltip from '@material-ui/core/Tooltip';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from "@material-ui/core/styles";
import RotateLeftIcon from '@material-ui/icons/RotateLeft';

import UploadField from './UploadField';
import { FormStepContext } from './FormStep';
import currentUser from "../../store/currentUser";


const LightTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    fontSize: 15,
    padding: 15,
    minWidth: 250,
    color: '#727272',
  },
}))(Tooltip);

@observer
class FormField extends React.Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    options: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
    area: PropTypes.bool,
    resetValue: PropTypes.any,
    resetTitle: PropTypes.string,
    onChange: PropTypes.func,
    serializer: PropTypes.func,
    deserializer: PropTypes.func,
  };
  static defaultProps = {
    options: null,
    area: false,
    resetValue: null,
    resetTitle: null,
    onChange: null,
    serializer: null,
    deserializer: null,
  };
  static contextType = FormStepContext;

  componentDidMount() {
    this.getContext().fields.push(this.props.name);
  }

  getContext() {
    if (this.context === null) {
      throw Error('No form step context found in FormField ancestors.');
    }
    return this.context;
  }

  get instance() {
    return this.getContext().instance;
  }

  @computed get value() {
    const { name, serializer } = this.props;
    let value = this.instance[name];
    if (serializer !== null) {
      value = serializer(value);
    }
    if (value === undefined || value === null) {
      value = '';
    }
    return value;
  }

  set value(value) {
    const { name, deserializer } = this.props;
    if (deserializer !== null) {
      value = deserializer(value);
    }
    this.instance[name] = value;
  }

  get initialValue() {
    let initialValue = 'initial' in this.fieldData ? this.fieldData.initial : '';
    const { serializer } = this.props;
    if (serializer !== null) {
      initialValue = serializer(initialValue);
    }
    return initialValue;
  }

  get canResetValue() {
    let { resetValue } = this.props;
    if (resetValue === null) {
      return false;
    }
    if (typeof resetValue === 'boolean') {
      if (!resetValue) {
        return false;
      }
      resetValue = this.initialValue;
    }
    return this.value.toString() !== resetValue.toString();
  }

  onResetValue = () => {
    const { resetValue } = this.props;
    if (typeof resetValue === 'boolean') {
      if (resetValue) {
        this.value = this.initialValue;
      }
    } else {
      this.value = resetValue;
    }
  };

  get fieldData() {
    const fieldsData = this.instance.constructor.fieldsData;
    if (fieldsData === null) {
      return null;
    }
    const { name } = this.props;
    if (name in fieldsData) {
      return fieldsData[name];
    }
    throw new Error(`“${name}” is not a field name found in the API.`);
  }

  get options() {
    let { options, serializer } = this.props;
    if (options === null) {
      const fieldData = this.fieldData;
      if (fieldData.type === 'choice') {
        options = {};
        for (const { value, display_name } of fieldData.choices) {
          options[value] = display_name;
        }
        if (!currentUser.is_superuser && currentUser.can('athena.scenarios_limited') && fieldData.label === 'Type') {
          options = Object.entries(options).reduce((prev, [key, val]) => {
            if (val === 'Simulation' || val === 'Simulation (reduced scope)' || val === 'Unconstrained Capacity' || val === 'Draft') {
              return prev;
            }
            prev[key] = val;
            return prev;
          }, {});
        }
      }
    }
    if (options !== null && serializer !== null) {
      options = options.map(([value, label]) => [serializer(value), label]);
    }
    return options;
  }

  get isCheckbox() {
    return this.fieldData.type === 'boolean';
  }

  get isFile() {
    return this.fieldData.type === 'file upload';
  }

  @computed get errorMessages() {
    let errorMessages = get(this.instance.formErrors, this.props.name);
    if (errorMessages === undefined) {
      return [];
    }
    return [...errorMessages];
  }

  @computed get errorText() {
    if (this.errorMessages.length === 0) {
      return null;
    }
    return this.errorMessages.reduce((array, message) => (
      array.length === 0 ? [message] : [...array, <br />, message]
      ),
      [],
    );
  }

  removeError() {
    if (this.errorMessages.length > 0) {
      remove(this.instance.formErrors, this.props.name);
    }
  }

  onChange = event => {
    const { name, deserializer, onChange } = this.props;
    let value;
    if (this.isCheckbox) {
      value = event.target.checked;
    } else if (this.isFile) {
      value = event.target.files;
    } else {
      value = event.target.value;
    }
    if (deserializer !== null) {
      value = deserializer(value);
    }
    this.removeError();
    this.instance[name] = value;
    if (onChange !== null) {
      onChange(event, this.instance);
    }
  };

  render() {
    const fieldData = this.fieldData;
    if (fieldData === null) {
      return null;
    }
    const props = {...this.props};
    const { resetTitle } = props;
    delete props.area;
    delete props.resetValue;
    delete props.resetTitle;
    delete props.serializer;
    delete props.deserializer;
    props.label = fieldData.label;
    props.helperText = this.errorText;
    props.error = this.errorMessages.length > 0;
    props.onChange = this.onChange;
    props.value = this.value;
    if (this.isCheckbox) {
      return (
        <FormControl disabled={props.disabled}>
          <FormControlLabel
            control={
              <Checkbox checked={props.value} onChange={props.onChange} />
            }
            label={props.label}
          />
          {
            props.helperText
            ? (
              <FormHelperText error={props.error}>{props.helperText}</FormHelperText>
            )
            : null
          }
        </FormControl>
      );
    }
    if (this.canResetValue) {
      props.InputProps = {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={resetTitle === null ? 'Reset to default value' : resetTitle}>
              <IconButton onClick={this.onResetValue} disabled={props.disabled}>
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      };
    }
    const options = this.options;
    const tooltipProps = {
      title: this.fieldData.help_text || '',
      placement: 'top',
      interactive: true,
    };
    if (options !== null) {
      let menuItems;
      let optionsArray = Array.isArray(options) ? options : Object.entries(options);
      if (optionsArray.length > 0) {
        menuItems = optionsArray.map(([value, label]) =>
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        );
      } else {
        menuItems = <MenuItem disabled>No option available</MenuItem>;
      }
      return (
        <LightTooltip {...tooltipProps}>
          <TextField select variant="outlined" {...props}>
            {menuItems}
          </TextField>
        </LightTooltip>
      );
    }
    if (this.isFile) {
      return (
        <UploadField {...props} />
      );
    }
    if (fieldData.type === 'integer') {
      props.type = 'number';
      props.inputProps = {min: fieldData.min_value, max: fieldData.max_value, step: 1};
    }
    else if (fieldData.type === 'string') {
      props.multiline = !fieldData.max_length;
      props.inputProps = {maxLength: fieldData.max_length};
      if (this.props.area){
        return (
          <LightTooltip {...tooltipProps}>
            <TextField variant="outlined" {...props} />
          </LightTooltip>
        );
      }
    }
    return (
      <LightTooltip {...tooltipProps}>
        <TextField variant="outlined" {...props} />
      </LightTooltip>
    );
  }
}


export default FormField;
